import React, { useCallback, useMemo } from 'react';
import { InputMax, Row, Box, Column } from '../../core';
import { Whitelist, VaultFull, Disclaimer } from '../../alerts';
import {
  useERC20,
  useCall2,
  useSend,
  toBN,
  formatAmountBN,
  PnlLock,
  useInputState,
  VaultUpgradable,
  useIsFull,
} from '@sc1/common';
import { StatusButton } from '../../StatusButton';
import { LockFeeInfo } from '../LockFeeInfo';

interface ScionDepositProps {
  vault: VaultUpgradable;
  account: string;
}

export const ScionDeposit = ({
  vault,
  account,
}: ScionDepositProps): JSX.Element => {
  const { amountStr, amountBN, setAmountWithMax, setAmount } = useInputState();

  const { sendTx, txStatus } = useSend();
  const { sendTx: sendTx1, txStatus: txStatus1 } = useSend();

  const { res: underlyingAddress } = useCall2(vault?.UNDERLYING);
  const underlying = useERC20(underlyingAddress);
  const { res: underlyingSymbol } = useCall2(underlying?.symbol);
  const { res: dec } = useCall2(underlying?.decimals);

  const { res: allowance = toBN(0) } = useCall2(
    underlying?.allowance,
    account,
    vault?.address
  );
  const { res: balanceBN = toBN(0) } = useCall2(underlying?.balanceOf, account);
  const balanceFmt = formatAmountBN(balanceBN, dec, false);
  const { res: maxTvlBN = toBN(0) } = useCall2(vault?.getMaxTvl);
  const { res: totalHoldingsBN = toBN(0) } = useCall2(vault?.totalHoldings);
  const { res: isAllowed } = useCall2(vault?.isAllowed, account);
  const { res: isManager } = useCall2(vault?.isManager, account);
  const { res: isPublic } = useCall2(vault?.isPublic);

  const userCanDeposit = isAllowed || isManager || isPublic;

  const { maxBN, isFull } = useIsFull(totalHoldingsBN, maxTvlBN, balanceBN);

  const approve = useCallback(
    () =>
      underlying && sendTx1(underlying, 'approve', [vault?.address, amountBN]),
    [underlying, amountBN, vault, sendTx1]
  );

  const deposit = useCallback(
    () => vault && amountBN.gt(0) && sendTx(vault, 'deposit', [amountBN]),
    [amountBN, vault, sendTx]
  );

  const isApproved = useMemo(
    () => allowance.gte(amountBN),
    [allowance, amountBN]
  );
  return (
    <Column sx={{ flex: 1 }}>
      <Box sx={{ fontSize: 1 }}>
        Available: {balanceFmt} {underlyingSymbol}
      </Box>
      <InputMax
        my={2}
        value={amountStr}
        type="number"
        placeholder="0"
        min="0"
        onMaxClick={() => {
          setAmount(maxBN, dec);
        }}
        onChange={(e) => {
          setAmountWithMax(e.target.value, dec, maxBN);
        }}
      />
      {isFull ? (
        <VaultFull />
      ) : !userCanDeposit ? (
        <Whitelist />
      ) : (
        <>
          <Row>
            {isApproved ? (
              <StatusButton
                sx={{ flex: 1 }}
                onClick={deposit}
                status={txStatus}
              >
                Deposit
              </StatusButton>
            ) : (
              <StatusButton
                sx={{ flex: 1 }}
                onClick={approve}
                status={txStatus1}
              >
                Approve
              </StatusButton>
            )}
          </Row>
          {isApproved ? (
            <LockFeeInfo
              vault={vault}
              decimals={dec}
              lockType={PnlLock.Deposit}
            />
          ) : (
            <Disclaimer />
          )}
        </>
      )}
    </Column>
  );
};
